import { createAsyncThunk } from "@reduxjs/toolkit";
import { FMSLogger } from "FMSLogger";
import { toast } from "react-toastify";

import API, { showNetworkErrorToast } from "Utils/api";
import { MISSIONS_ACTIONS } from "Utils/constants";

import { RowData } from "Components/NewList/types";
import {
  reorderMissions,
  setMissions,
  setMissionHot,
  clearMissionHot,
  UNASSIGNED_MISSIONS_KEY,
} from ".";

const missionsActionsLogger = FMSLogger.byPrefix(MISSIONS_ACTIONS);

export const fetchMissions = createAsyncThunk<
  void,
  {
    currentFleetId: string;
  }
>(
  "fetchMissions",
  async (
    data: {
      currentFleetId: string;
    },
    thunkAPI
  ) => {
    try {
      // Request queue/execution order explicitly: hot jobs lead (-priority),
      // then FIFO queue position (order). The server default is -time_stamp
      // (newest first), which renders the queue in reverse execution order.
      const url = `/fleets/mission_queue/?status=deployed&fleet_ids=${data.currentFleetId}&ordering=-priority,order,-time_stamp`;
      const response = await API.get(url);
      const results = response?.data?.results;
      thunkAPI.dispatch(setMissions(results));
    } catch (error) {
      missionsActionsLogger.warn("[QM] fetchMissions ERROR", String(error));
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const cancelLiveAndQueueMission = createAsyncThunk<
  void,
  { missionId: string; vehicleId: string | null | undefined }
>(
  "missionPage/cancel",
  async (data: { missionId: string; vehicleId: string | null | undefined }) => {
    try {
      // Uber missions (Call Vehicle) have no assigned vehicle until the
      // assignment engine picks one — vehicleId is null/undefined. The
      // vehicle-scoped cancel URL would interpolate the literal string "null"
      // and 404. Fall back to the fleet-scoped cancel in that case.
      // Queue Manager passes UNASSIGNED_MISSIONS_KEY ("unassigned") as the
      // vehicleId for missions in the Fleet Unassigned section; that sentinel
      // must also route to the fleet-scoped endpoint.
      const hasVehicle =
        data.vehicleId &&
        data.vehicleId !== "null" &&
        data.vehicleId !== "undefined" &&
        data.vehicleId !== UNASSIGNED_MISSIONS_KEY;
      const url = hasVehicle
        ? `vehicles/${data.vehicleId}/mission_queue/${data.missionId}/cancel/`
        : `fleets/mission_queue/${data.missionId}/cancel/`;
      await API.post(url);
      toast.success("Mission cancelled successfully");
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const emergencyStopFleet = createAsyncThunk<void, { fleetId: string }>(
  "missionQueue/emergencyStop",
  async (data: { fleetId: string }, thunkAPI) => {
    try {
      await API.post("fleets/mission_queue/emergency_stop/", {
        fleet_id: data.fleetId,
      });
      toast.success("All fleet missions cancelled");
      thunkAPI.dispatch(fetchMissions({ currentFleetId: data.fleetId }));
    } catch (error) {
      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const changeMissionOrder = createAsyncThunk<
  void,
  {
    missionId: string;
    order: number;
    cancelAction: () => RowData[];
    newMissionsOrder: RowData[];
  }
>(
  "missionQueue",
  async (
    data: {
      missionId: string;
      order: number;
      cancelAction: () => RowData[];
      newMissionsOrder: RowData[];
    },
    thunkAPI
  ) => {
    thunkAPI.dispatch(
      reorderMissions(data.newMissionsOrder.map((item) => item.id))
    );
    try {
      await API.patch(`fleets/mission_queue/${data.missionId}/`, {
        order: data.order,
      });
      toast.success("Mission reordered successfully");
    } catch (error) {
      const oldItems = data.cancelAction();
      thunkAPI.dispatch(reorderMissions(oldItems.map((item) => item.id)));

      showNetworkErrorToast(error);
      return Promise.reject();
    }
  }
);

export const reorderFleetQueue = createAsyncThunk<
  void,
  {
    missionId: string;
    newIndex: number;
    newVehicleId?: string; // optional — triggers vehicle reassignment on the backend
  }
>("missionQueue/reorderFleetQueue", async (data) => {
  try {
    await API.post("fleets/mission_queue/reorder/", {
      mission_id: data.missionId,
      new_index: data.newIndex,
      ...(data.newVehicleId ? { new_vehicle_id: data.newVehicleId } : {}),
    });
    toast.success(
      data.newVehicleId
        ? "Mission reassigned successfully"
        : "Mission reordered successfully"
    );
  } catch (error) {
    showNetworkErrorToast(error);
    return Promise.reject(error);
  }
});

export const setHotJob = createAsyncThunk<
  void,
  { missionId: string; isCurrentlyHot: boolean }
>("missionQueue/setHotJob", async (data, thunkAPI) => {
  // Snapshot mission order before optimistic update so we can restore on failure
  const state = thunkAPI.getState() as {
    missionsPage: { missions: Mission[] | null };
  };
  const savedIds = state.missionsPage.missions?.map((m) => m.id) ?? [];

  // Optimistic update: apply state change immediately for instant UI feedback
  if (data.isCurrentlyHot) {
    thunkAPI.dispatch(clearMissionHot(data.missionId));
  } else {
    thunkAPI.dispatch(setMissionHot(data.missionId));
  }
  try {
    await API.post(`fleets/mission_queue/${data.missionId}/hot-job/`);
  } catch (error) {
    // Rollback: revert both the hot flag and the position change
    if (data.isCurrentlyHot) {
      thunkAPI.dispatch(setMissionHot(data.missionId));
    } else {
      thunkAPI.dispatch(clearMissionHot(data.missionId));
    }
    thunkAPI.dispatch(reorderMissions(savedIds));
    showNetworkErrorToast(error);
    return Promise.reject(error);
  }
});
